<template>
  <div class="dictionary">
    <a-card :bordered="false">
      <!-- 标题 -->
      <template #title>
        <menu-outlined />
        <span style="margin-left: 10px">{{ $t('Dictionaries.DictionaryMainRight.dictionaryEntries') }}</span>
        <div class="head-btn">
          <a-button @click="create"
                    v-if="store.dictionarySingleKey && store.dictionarySingleId"
                    data-cy="cy-btn-create-item">
            <plus-outlined />{{ $t('Dictionaries.DictionaryMainRight.createEntry') }}
          </a-button>
        </div>
      </template>
      <!-- 表格 -->
      <div class="table">
        <a-scroll-table class="scroll"
                        data-cy="cy-table-item"
                        v-if="store.dictionarySingleKey && store.dictionarySingleId"
                        :columns="ITEM_COLUMNS"
                        :data-source="itemSource"
                        :pagination="pagination"
                        @change="handleTableChange"
                        rowKey="id">
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'operation'">
              <a @click="editRow(record)"
                 data-cy="cy-edit-item">{{ $t('Dictionaries.DictionaryMainRight.edit') }}</a>
              <a @click="deleteRow(record)"
                 data-cy="cy-delete-item"
                 style="margin-left:10px">{{ $t('Dictionaries.DictionaryMainRight.delete') }}</a>
            </template>
          </template>
        </a-scroll-table>
      </div>
    </a-card>
    <a-modal v-model:visible="visible"
             :title="title"
             @ok="onCancelOk"
             :confirm-loading="confirmLoading"
             @cancel="onCancel">
      <a-form :model="addFormState"
              name="basic"
              data-cy="cy-modal-form"
              ref="aFrom"
              :label-col="{ span: 6 }"
              autocomplete="off">
        <a-form-item :label="$t('Dictionaries.DictionaryMainRight.entryKey')"
                     name="key"
                     :rules="[{ required: true, message: $t('Dictionaries.DictionaryMainRight.enterEntryKey') }]">
          <a-input v-model:value="addFormState.key"
                   data-cy="cy-input-key-item"
                   :placeholder="$t('Dictionaries.DictionaryMainRight.entryKey')" />
        </a-form-item>
        <a-form-item :label="$t('Dictionaries.DictionaryMainRight.entryName')"
                     name="name"
                     :rules="[{ required: true, message: $t('Dictionaries.DictionaryMainRight.enterEntryName') }]">
          <a-input v-model:value="addFormState.name"
                   data-cy="cy-input-key-name"
                   :placeholder="$t('Dictionaries.DictionaryMainRight.entryName')" />
        </a-form-item>
        <a-form-item :label="$t('Dictionaries.DictionaryMainRight.entryIndex')"
                     name="index"
                     :rules="[{ required: true, message: $t('Dictionaries.DictionaryMainRight.enterEntryIndex') }]">
          <a-input v-model:value="addFormState.index"
                   data-cy="cy-input-key-number"
                   :placeholder="$t('Dictionaries.DictionaryMainRight.entryIndex')" />
        </a-form-item>
      </a-form>
    </a-modal>
    <!-- 删除弹出框 -->
    <a-modal v-model:visible="delVisible"
             :title="$t('Dictionaries.DictionaryMainRight.deleteEntryTitle')"
             @ok="onDelModalOk"
             :confirm-loading="loading"
             @cancel="delVisible = false">
      <p style="font-size:16px">{{ $t('Dictionaries.DictionaryMainRight.deleteEntryConfirm') }}{{ addFormState.name }}</p>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { AScrollTable } from '@iota-cn/ui-components'
import { watch, ref, reactive } from 'vue'
import { dictionaryEntryList, dictionaryEntryAdd, dictionaryEntryEdit, dictionaryEntryDelete } from '../service/DictionaryService'
import type { DictionaryEntrySingle } from '../service/type/DictionaryType'
import { ITEM_COLUMNS } from './index'
import { dictionaryStore } from '../DictionaryPlugin'
import type { FormInstance } from 'ant-design-vue/es/form/Form'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

const { t } = useI18n()
const store = dictionaryStore()

watch(
  () => store.dictionarySingleId, () => {
    fetchDictionaryEntry()
  },
  { deep: true }
)
const confirmLoading = ref<boolean>(false)
const loading = ref<boolean>(false)
const addFormState = reactive<DictionaryEntrySingle>({
  index: null,
  key: '',
  name: ''
})
const itemSource = ref<DictionaryEntrySingle[]>([])
const fetchDictionaryEntry = async () => {
  const res = await dictionaryEntryList(store.dictionarySingleKey)
  itemSource.value = res.items
}
const visible = ref<boolean>(false)
const aFrom = ref<FormInstance | null>()
const create = () => {
  visible.value = true
  title.value = t('Dictionaries.DictionaryMainRight.newEntry')
  visibleType.value = 1
}
const title = ref<string>('')
const visibleType = ref<number>()
// 分页
const pagination = ref({
  total: 0,
  pageSize: 20,
  showSizeChanger: true,
  pageSizeOptions: ['20', '40', '60', '80'], // v-loading=""每页中显示的数据
  showTotal: (total: number) => t('Dictionaries.DictionaryMainRight.totalEntries', { total }), // 分页中显示总的数据
  size: 'small',
})
const handleTableChange = (event: any) => {
  pagination.value.pageSize = event.pageSize
}
const onCancelOk = () => {
  aFrom.value?.validate().then(async (res: any) => {
    if (res.outOfDate) {
      return
    }
    confirmLoading.value = true
    try {
      if (visibleType.value == 1) {
        await dictionaryEntryAdd(store.dictionarySingleId, addFormState)
      } else {
        editData.value.key = addFormState.key
        editData.value.name = addFormState.name
        editData.value.index = addFormState.index
        await dictionaryEntryEdit(store.dictionarySingleId, editData.value)
      }
      onCancel()
      await fetchDictionaryEntry()
    } catch (error: any) {
      if (error.message === 'item exist with same key') {
        message.error('当前key重复')
      } else {
        message.error('有异常请联系管理员')
      }
    }
    confirmLoading.value = false
  }).catch()
}
const onCancel = () => {
  aFrom.value?.resetFields()
  visible.value = false
  addFormState.key = ''
  addFormState.name = ''
  addFormState.index = null
}
const editData = ref<DictionaryEntrySingle>({
  id: '',
  key: '',
  name: '',
  index: 0
})
const editRow = (record: any) => {
  visible.value = true
  title.value = t('Dictionaries.DictionaryMainRight.editEntry')
  addFormState.key = record.key
  addFormState.name = record.name
  addFormState.index = record.index
  editData.value = record
  visibleType.value = 2
}
const delVisible = ref<boolean>(false)
const deleteData = ref<DictionaryEntrySingle>({
  id: '',
  key: '',
  name: '',
  index: null
})
const deleteRow = (record: any) => {
  delVisible.value = true
  addFormState.name = record.name
  deleteData.value = record
}
const onDelModalOk = async () => {
  loading.value = true
  await dictionaryEntryDelete(store.dictionarySingleId, deleteData.value)
  loading.value = false
  delVisible.value = false
  await fetchDictionaryEntry()
}
</script>


<style lang="less" scoped>
.dictionary {
  .scroll {
    height: calc(100vh - 330px);
  }

  .head-btn {
    float: right;
  }
}
</style>
